/**
 * Normalising the two account fields that have a check constraint behind them
 * — RF-0003.
 *
 * `accounts.website` is `check (website is null or website ~ '^https://')` and
 * `accounts.hq_country` is `check (hq_country is null or hq_country ~
 * '^[A-Z]{2}$')`. A form that posts `example.com` or `us` earns a raw `23514`
 * naming the constraint, which is not a sentence anyone should be shown.
 *
 * **One module, two callers.** The create dialog and the account edit dialog
 * both go through this, so there is one answer to "what does this field have
 * to look like" rather than one per form. `end_use_country` on
 * `OpportunityDetail` already upper-cases on the way in; this is the same
 * move, named and shared.
 *
 * Normalise where it is unambiguous and refuse where it is not: a bare host
 * gains `https://`, a scheme is lower-cased, `http://` is upgraded because the
 * column admits nothing else, and any other scheme is rejected rather than
 * mangled into one.
 */

export interface Normalised {
  /** `null` clears the column. */
  value: string | null;
  /** Non-null means do not submit; show this against the field. */
  error: string | null;
}

const ok = (value: string | null): Normalised => ({ value, error: null });
const bad = (error: string): Normalised => ({ value: null, error });

export function normaliseWebsite(raw: string): Normalised {
  const trimmed = raw.trim();
  if (trimmed === '') return ok(null);

  const scheme = /^([a-z][a-z0-9+.-]*):\/\//i.exec(trimmed);
  if (scheme === null) return ok(`https://${trimmed}`);

  const name = scheme[1]!.toLowerCase();
  // The column admits https and nothing else, so http is an upgrade rather
  // than a rejection. §9 forbids `javascript:` and `data:` outright, and they
  // have no `//` anyway — anything else that is not http(s) is refused.
  if (name === 'https' || name === 'http') {
    return ok(`https://${trimmed.slice(scheme[0]!.length)}`);
  }
  return bad('A website must be an https:// address.');
}

export function normaliseCountry(raw: string): Normalised {
  const trimmed = raw.trim().toUpperCase();
  if (trimmed === '') return ok(null);
  if (!/^[A-Z]{2}$/.test(trimmed)) {
    return bad('Use the two-letter ISO country code, such as US or DE.');
  }
  return ok(trimmed);
}

/** Free text: trimmed, and empty means "clear the column" rather than `''`. */
export function optionalText(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}
